/**
 * PlanLibrary — modal for selecting, creating, editing and deleting plans.
 *
 * Shows two sections: My Plans (user-created) and App Plans (read-only seeds).
 * Selecting any row sets activeSavedPlanId and closes the modal.
 */

import { type ReactNode, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { APP_PLANS, MVP_NODES, countOverdueOutsidePlan, type SavedPlan } from "@music-app/learning-system";
import { usePrefsStore, useSavedPlansStore } from "@music-app/persistence";
import { useCardsStore } from "@music-app/persistence";

interface PlanLibraryProps {
  onClose: () => void;
}

export function PlanLibrary({ onClose }: PlanLibraryProps): ReactNode {
  const activeSavedPlanId = usePrefsStore((s) => s.activeSavedPlanId);
  const setActiveSavedPlanId = usePrefsStore((s) => s.setActiveSavedPlanId);
  const userPlans = useSavedPlansStore((s) => s.plans);
  const deletePlanStore = useSavedPlansStore((s) => s.deletePlan);
  const cards = useCardsStore((s) => s.cards);

  const activePlan = [...userPlans, ...APP_PLANS].find((p) => p.id === activeSavedPlanId)
    ?? APP_PLANS[0]!;
  const overdueCount = countOverdueOutsidePlan(activePlan, cards, MVP_NODES);

  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const handleSelect = useCallback(
    (id: string) => {
      void setActiveSavedPlanId(id);
      onClose();
    },
    [setActiveSavedPlanId, onClose],
  );

  const handleDelete = useCallback(
    async (id: string) => {
      await deletePlanStore(id);
      if (activeSavedPlanId === id) {
        await setActiveSavedPlanId("app-standard");
      }
      setConfirmDeleteId(null);
    },
    [deletePlanStore, activeSavedPlanId, setActiveSavedPlanId],
  );

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40"
      role="dialog"
      aria-modal="true"
      aria-label="Plan library"
    >
      <div className="bg-paper border border-ink/15 w-full max-w-sm mx-4 max-h-[80vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-ink/10">
          <h2 className="font-mono text-[10px] uppercase tracking-[0.18em]">Plans</h2>
          <button
            type="button"
            onClick={onClose}
            className="font-mono text-sm text-inkFaded hover:text-ink transition-colors"
            aria-label="Close plan library"
          >
            ×
          </button>
        </div>

        <div className="overflow-y-auto flex-1">
          {/* Overdue-outside-plan callout */}
          {overdueCount > 0 && (
            <div className="mx-4 mt-3 px-3 py-2 border border-warning/30 bg-warning/8">
              <p className="font-mono text-[9px] uppercase tracking-widest text-warning mb-0.5">
                Outside this plan
              </p>
              <p className="font-serif italic text-[13px] text-inkSoft">
                {overdueCount} item{overdueCount !== 1 ? "s" : ""} outside this plan&apos;s scope{" "}
                {overdueCount === 1 ? "is" : "are"} overdue.{" "}
                <button
                  type="button"
                  className="underline text-ink hover:text-accent transition-colors"
                  onClick={() => void setActiveSavedPlanId("app-standard").then(onClose)}
                >
                  Switch to Standard
                </button>
                {" "}to clear them.
              </p>
            </div>
          )}

          {/* My Plans */}
          <section aria-label="My plans">
            <p className="px-4 pt-3 pb-1 font-mono text-[9px] uppercase tracking-widest text-inkFaded">
              My Plans
            </p>
            {userPlans.length === 0 ? (
              <p className="px-4 py-2 font-serif italic text-sm text-inkSoft">
                No custom plans yet.
              </p>
            ) : (
              userPlans.map((plan) => (
                <UserPlanRow
                  key={plan.id}
                  plan={plan}
                  active={activeSavedPlanId === plan.id}
                  confirmingDelete={confirmDeleteId === plan.id}
                  onSelect={() => handleSelect(plan.id)}
                  onDelete={() => setConfirmDeleteId(plan.id)}
                  onConfirmDelete={() => void handleDelete(plan.id)}
                  onCancelDelete={() => setConfirmDeleteId(null)}
                />
              ))
            )}
            <Link
              to="/review/plans/new"
              onClick={onClose}
              className="flex items-center px-4 py-2.5 font-mono text-[10px] text-accent hover:bg-paperWarm transition-colors border-t border-ink/10"
            >
              + Create a plan
            </Link>
          </section>

          {/* App Plans */}
          <section aria-label="App plans" className="border-t border-ink/10">
            <p className="px-4 pt-3 pb-1 font-mono text-[9px] uppercase tracking-widest text-inkFaded">
              App Plans
            </p>
            {APP_PLANS.map((plan) => (
              <AppPlanRow
                key={plan.id}
                plan={plan}
                active={activeSavedPlanId === plan.id}
                onSelect={() => handleSelect(plan.id)}
                onDuplicate={onClose}
              />
            ))}
          </section>
        </div>
      </div>
    </div>
  );
}

// ─── UserPlanRow ──────────────────────────────────────────────────────────────

function UserPlanRow({
  plan,
  active,
  confirmingDelete,
  onSelect,
  onDelete,
  onConfirmDelete,
  onCancelDelete,
}: {
  plan: SavedPlan;
  active: boolean;
  confirmingDelete: boolean;
  onSelect: () => void;
  onDelete: () => void;
  onConfirmDelete: () => void;
  onCancelDelete: () => void;
}): ReactNode {
  return (
    <div className={`flex items-center px-4 py-2.5 border-b border-ink/10 hover:bg-paperWarm transition-colors ${active ? "bg-paperWarm" : ""}`}>
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 text-left"
        aria-pressed={active}
        aria-label={`${active ? "Active: " : ""}${plan.name}`}
      >
        <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${active ? "text-accent" : "text-ink"}`}>
          {active ? "● " : "○ "}{plan.name}
        </span>
      </button>
      {confirmingDelete ? (
        <span className="flex items-center gap-2 ml-2">
          <button
            type="button"
            onClick={onConfirmDelete}
            className="font-mono text-[9px] uppercase text-red-500 hover:text-red-700 transition-colors"
          >
            Delete
          </button>
          <button
            type="button"
            onClick={onCancelDelete}
            className="font-mono text-[9px] uppercase text-inkFaded hover:text-ink transition-colors"
          >
            Cancel
          </button>
        </span>
      ) : (
        <span className="flex items-center gap-2 ml-2">
          <Link
            to={`/review/plans/${plan.id}/edit`}
            className="font-mono text-[9px] uppercase text-inkFaded hover:text-ink transition-colors"
          >
            Edit
          </Link>
          <button
            type="button"
            onClick={onDelete}
            className="font-mono text-[9px] uppercase text-inkFaded hover:text-red-500 transition-colors"
            aria-label={`Delete ${plan.name}`}
          >
            ×
          </button>
        </span>
      )}
    </div>
  );
}

// ─── AppPlanRow ───────────────────────────────────────────────────────────────

function AppPlanRow({
  plan,
  active,
  onSelect,
  onDuplicate,
}: {
  plan: SavedPlan;
  active: boolean;
  onSelect: () => void;
  onDuplicate: () => void;
}): ReactNode {
  return (
    <div className={`flex items-center px-4 py-2.5 border-b border-ink/10 hover:bg-paperWarm transition-colors ${active ? "bg-paperWarm" : ""}`}>
      <button
        type="button"
        onClick={onSelect}
        className="flex-1 text-left"
        aria-pressed={active}
        aria-label={`${active ? "Active: " : ""}${plan.name}`}
      >
        <span className={`font-mono text-[10px] uppercase tracking-[0.18em] ${active ? "text-accent" : "text-inkSoft"}`}>
          {active ? "● " : "○ "}{plan.name}
        </span>
      </button>
      <Link
        to={`/review/plans/new?duplicateFrom=${plan.id}`}
        onClick={onDuplicate}
        className="font-mono text-[9px] uppercase text-inkFaded hover:text-ink transition-colors ml-2"
      >
        Duplicate
      </Link>
    </div>
  );
}
